import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Check, Sparkles } from 'lucide-react';
import '../../../assets/styles/customer/layout/PricingSection.css';
import MyButton from '../../../components/common/Button/MyButton';

const PricingSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const pricingPlans = [
    {
      id: 1,
      name: 'Free',
      price: '0',
      period: '/month',
      range: 'Up to 10 customers',
      features: [
        '10 customers included',
        'QR code delivery',
        'Daily tracking',
        'Basic billing',
        'Mobile app access'
      ],
      buttonText: 'Get Started',
      buttonVariant: 'outline',
      popular: false,
      color: '#6c757d'
    },
    {
      id: 2,
      name: 'Basic',
      price: '1,500',
      period: '/month',
      range: '11-30 customers',
      features: [
        'Up to 30 customers',
        'All Free features',
        'Priority support',
        'Advanced analytics',
        'Payment tracking'
      ],
      buttonText: 'Get Started',
      buttonVariant: 'primary',
      popular: true,
      color: '#3e96e7'
    },
    {
      id: 3,
      name: 'Standard',
      price: '2,500',
      period: '/month',
      range: '31-60 customers',
      features: [
        'Up to 60 customers',
        'All Basic features',
        'Route optimization',
        'Bulk SMS notifications',
        'Custom reports'
      ],
      buttonText: 'Get Started',
      buttonVariant: 'outline',
      popular: false,
      color: '#4caf50'
    },
    {
      id: 4,
      name: 'Premium',
      price: '5,000',
      period: '/month',
      range: '60+ customers',
      features: [
        'Unlimited customers',
        'All Standard features',
        'Dedicated support',
        'API access',
        'White-label option'
      ],
      buttonText: 'Get Started',
      buttonVariant: 'outline',
      popular: false,
      color: '#9c27b0'
    }
  ];

  return (
    <section className="pricing-section">
      
      {/* Floating Milk Bottles */}
      <div className="pricing-floating-milk">
        <div className="price-bottle bottle-1">🥛</div>
        <div className="price-bottle bottle-2">🥛</div>
        <div className="price-bottle bottle-3">🥛</div>
        <div className="price-bottle bottle-4">🥛</div>
        <div className="price-bottle bottle-5">🥛</div>
      </div>

      {/* Wavy Background Layers */}
      <div className="pricing-waves">
        <svg className="pricing-wave wave-top" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="rgba(62, 150, 231, 0.04)" d="M0,160L48,154.7C96,149,192,139,288,154.7C384,171,480,213,576,213.3C672,213,768,171,864,138.7C960,107,1056,85,1152,96C1248,107,1344,149,1392,170.7L1440,192L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
        </svg>
        <svg className="pricing-wave wave-bottom" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="rgba(76, 175, 80, 0.03)" d="M0,96L48,106.7C96,117,192,139,288,138.7C384,139,480,117,576,122.7C672,128,768,160,864,160C960,160,1056,128,1152,117.3C1248,107,1344,117,1392,122.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>

      <Container>
        
        {/* Section Header */}
        <div className="pricing-header text-center">
          <h2 className="pricing-title">
            Simple, Transparent <span className="pricing-title-highlight">Pricing</span>
          </h2>
          <p className="pricing-subtitle">
            Start free and scale as your business grows
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <Row className="g-4 pricing-cards-row">
          {pricingPlans.map((plan, index) => (
            <Col lg={3} md={6} sm={6} xs={12} key={plan.id}>
              <div 
                className={`pricing-card ${plan.popular ? 'popular-card' : ''} ${hoveredCard === plan.id ? 'hovered' : ''}`}
                style={{ 
                  animationDelay: `${index * 0.15}s`,
                  '--plan-color': plan.color
                }}
                onMouseEnter={() => setHoveredCard(plan.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="popular-badge">
                    <Sparkles size={16} />
                    <span>Most Popular</span>
                  </div>
                )}

                {/* Plan Name */}
                <h3 className="plan-name">{plan.name}</h3>

                {/* Price */}
                <div className="plan-price">
                  <span className="currency">Rs</span>
                  <span className="amount">{plan.price}</span>
                  <span className="period">{plan.period}</span>
                </div>

                {/* Customer Range */}
                <p className="plan-range">{plan.range}</p>

                {/* Features List */}
                <ul className="features-list">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="feature-item">
                      <div className="feature-check">
                        <Check size={16} strokeWidth={3} />
                      </div>
                      <span className="feature-text">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                {plan.buttonVariant === 'primary' ? (
                  <MyButton 
                    variant="primary" 
                    className="pricing-btn w-100"
                  >
                    {plan.buttonText}
                  </MyButton>
                ) : (
                  <button className="pricing-btn-outline w-100">
                    {plan.buttonText}
                  </button>
                )}

                {/* Decorative Corner */}
                <div className="card-corner-decoration"></div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Bottom Note */}
        <div className="pricing-footer text-center">
          <p className="footer-note">
            All plans include 30-day money-back guarantee. No credit card required for Free plan.
          </p>
        </div>

      </Container>
    </section>
  );
};

export default PricingSection;